import { useState } from 'react';
import { useAuth } from '../context/AuthContext.jsx';

export default function AuthPage() {
  const { login, register } = useAuth();
  const [tab, setTab]       = useState('login'); // 'login' | 'register'
  const [form, setForm]     = useState({ name: '', email: '', password: '', organization: '' });
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!form.email || !form.password) { setError('Email and password are required.'); return; }
    const domain = form.email.toLowerCase().split('@')[1];
    if (!domain || !['luminoidtech.com', 'krishigyanai.com'].includes(domain)) {
      setError('Access is restricted to @luminoidtech.com and @krishigyanai.com corporate emails.');
      return;
    }
    if (tab === 'register' && !form.name) { setError('Full name is required.'); return; }
    if (tab === 'register' && form.password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    try {
      if (tab === 'login') {
        await login(form.email, form.password);
      } else {
        await register(form.name, form.email, form.password, form.organization);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function switchTab(t) {
    setTab(t);
    setError('');
    setForm({ name: '', email: '', password: '', organization: '' });
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#f8fafc', padding: '24px',
    }}>
      <div style={{
        width: '100%', maxWidth: 1040, display: 'grid', gridTemplateColumns: '1fr 1fr',
        background: '#ffffff', borderRadius: 28, overflow: 'hidden',
        border: '1px solid #e2e8f0', boxShadow: '0 20px 50px rgba(0,0,0,0.06)',
      }}>

        {/* Left Hero Showcase Panel */}
        <div style={{
          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
          padding: '52px 44px', color: '#ffffff', display: 'flex',
          flexDirection: 'column', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{
              width: 52, height: 52, borderRadius: 16,
              background: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(10px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 26, marginBottom: 28,
            }}>🤖</div>

            <h1 style={{ fontSize: 32, fontWeight: 900, lineHeight: 1.25, margin: '0 0 16px', letterSpacing: '-0.02em' }}>
              AI-Driven Talent Evaluation Platform
            </h1>
            <p style={{ fontSize: 15, color: '#bfdbfe', lineHeight: 1.7, margin: '0 0 36px' }}>
              Automate multi-domain candidate screening with real-time AI scoring, video capture, and personalized questions.
            </p>
          </div>

          {/* Feature Bullets */}
          <div style={{ display: 'grid', gap: 16 }}>
            {[
              { icon: '🎯', title: 'Precision Skill Matching', desc: 'Analyzes JD and candidate resume with Groq LLM' },
              { icon: '🎥', title: 'HD Studio Video Recording', desc: 'Captures candidate voice & video interview sessions' },
              { icon: '⚡', title: 'Real-Time Transcription', desc: 'Instant speech-to-text audio processing' },
            ].map((f) => (
              <div key={f.title} style={{
                display: 'flex', gap: 14, alignItems: 'center',
                background: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(10px)',
                padding: '12px 16px', borderRadius: 14, border: '1px solid rgba(255, 255, 255, 0.15)',
              }}>
                <span style={{ fontSize: 20 }}>{f.icon}</span>
                <div>
                  <p style={{ margin: '0 0 2px', fontSize: 13, fontWeight: 700, color: '#ffffff' }}>{f.title}</p>
                  <p style={{ margin: 0, fontSize: 11, color: '#93c5fd' }}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Authentication Form Panel */}
        <div style={{ padding: '52px 44px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          
          <div style={{ marginBottom: 28 }}>
            <h2 style={{ margin: '0 0 6px', fontSize: 24, fontWeight: 800, color: '#0f172a' }}>
              {tab === 'login' ? 'Welcome Back!' : 'Get Started Now'}
            </h2>
            <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
              {tab === 'login' ? 'Sign in to access your recruiter workspace' : 'Create your recruiter account to begin evaluation'}
            </p>
          </div>

          {/* Tab switcher */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6,
            background: '#f1f5f9', borderRadius: 14, padding: 5, marginBottom: 24,
          }}>
            {['login', 'register'].map((t) => (
              <button key={t} onClick={() => switchTab(t)} style={{
                padding: '10px', borderRadius: 10, border: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: 700, transition: 'all 0.2s',
                background: tab === t ? '#ffffff' : 'transparent',
                color: tab === t ? '#2563eb' : '#64748b',
                boxShadow: tab === t ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
              }}>
                {t === 'login' ? '🔑 Sign In' : '✨ Create Account'}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit}>
            {/* Register-only fields */}
            {tab === 'register' && (
              <>
                <Field label="Full Name" value={form.name} onChange={set('name')} placeholder="Jane Smith" autoFocus />
                <Field label="Organization" value={form.organization} onChange={set('organization')} placeholder="Acme Corp (optional)" />
              </>
            )}

            <Field
              label="Work Email (@luminoidtech.com / @krishigyanai.com)"
              type="email"
              value={form.email}
              onChange={set('email')}
              placeholder="you@luminoidtech.com or you@krishigyanai.com"
              autoFocus={tab === 'login'}
            />

            {/* Password with show/hide */}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPass ? 'text' : 'password'}
                  value={form.password}
                  onChange={set('password')}
                  placeholder={tab === 'register' ? 'Min. 6 characters' : '••••••••'}
                  style={{ ...inputStyle, paddingRight: 44 }}
                />
                <button type="button" onClick={() => setShowPass((s) => !s)} style={{
                  position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', fontSize: 16,
                  color: '#64748b', padding: 0,
                }}>
                  {showPass ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '12px 16px', borderRadius: 12, marginBottom: 20,
                background: '#fef2f2', border: '1px solid #fecaca',
                color: '#dc2626', fontSize: 13, fontWeight: 600,
              }}>
                <span>⚠</span> {error}
              </div>
            )}

            {/* Submit */}
            <button type="submit" disabled={loading} style={{
              width: '100%', padding: '14px', borderRadius: 14, border: 'none',
              background: loading ? '#93c5fd' : 'linear-gradient(135deg,#3b82f6,#1d4ed8)',
              color: '#ffffff', fontWeight: 800, fontSize: 15, cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: '0 4px 16px rgba(37,99,235,0.25)', transition: 'all 0.2s',
            }}>
              {loading ? (
                <>
                  <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#ffffff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                  {tab === 'login' ? 'Signing in…' : 'Creating account…'}
                </>
              ) : (
                tab === 'login' ? 'Sign In →' : 'Create Account →'
              )}
            </button>
          </form>

          {/* Footer note */}
          <div style={{ marginTop: 20, textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>
              {tab === 'login' ? "Don't have an account? " : 'Already registered? '}
              <button
                onClick={() => switchTab(tab === 'login' ? 'register' : 'login')}
                style={{ background: 'none', border: 'none', color: '#2563eb', fontWeight: 700, cursor: 'pointer', fontSize: 12, padding: 0 }}
              >
                {tab === 'login' ? 'Create one now' : 'Sign in here'}
              </button>
            </p>
          </div>

        </div>

      </div>
    </div>
  );
}

function Field({ label, type = 'text', value, onChange, placeholder, autoFocus }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={labelStyle}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoFocus={autoFocus}
        style={inputStyle}
      />
    </div>
  );
}

const labelStyle = {
  display: 'block',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#2563eb',
  marginBottom: 7,
};

const inputStyle = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: 12,
  border: '1px solid #e2e8f0',
  background: '#ffffff',
  color: '#0f172a',
  boxSizing: 'border-box',
  fontSize: 14,
  outline: 'none',
  transition: 'border-color 0.2s, box-shadow 0.2s',
};
